import '../styles/Users.css';
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Row, Col, Alert } from "react-bootstrap";
import MemberDto from "../Models/MemberDto";
import { getUsers, getUserLimit } from "../Services/AccessControlService";
import { useMsal } from "@azure/msal-react";
import { useApiErrorHandler } from "../Utilities/useApiErrorHandler";
import { LoaderIcon } from "../Components/Global/Icons";
import RecentActivity from "../Components/Global/RecentActivity";
import UsersTable from '../Components/Tables/UsersList';
import { getprojectforuser } from '../Services/PtaasApiService';
import { useLocation } from 'react-router-dom';
import { useUser } from '../Components/Global/RoleContext';
import AccessRestricted from '../Components/Global/UnauthorizedMessage';

const UsersManagementPage: React.FC = () => {
  const { instance, accounts } = useMsal();
  const [members, setMembers] = useState<MemberDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [projectsLoading, setProjectsLoading] = useState(false);
  const [allProjects, setAllProjects] = useState<any[]>([]);
  const [projectsError, setProjectsError] = useState<string | null>(null);
  const location = useLocation();
  const { highestPrivilegeRole } = useUser();
  const canManageUsers = highestPrivilegeRole === 'Super User' || highestPrivilegeRole === 'Project Manager';
  const [fetchError, setFetchError] = useState(false);
  const { handleApiError } = useApiErrorHandler();
  const [userLimitInfo, setUserLimitInfo] = useState({
    currentUsers: 0,
    maxUsers: 0,
  });
  const [warningMessage, setWarningMessage] = useState<string | null>(null);
  
  const lastMembersCount = useRef<number>(0);

  const fetchUsers = useCallback(async (): Promise<MemberDto[]> => {
    setLoading(true);
    try {
      const returnedmembers = await getUsers(instance, accounts?.[0]);
      setMembers(returnedmembers);
      return returnedmembers;
    } catch (err: any) {
      handleApiError(err);
      setFetchError(true);
      return [];
    } finally {
      setLoading(false);
    }
  }, [instance, accounts, handleApiError]);

  const fetchUserLimit = useCallback(async (): Promise<{ currentUsers: number; maxUsers: number } | null> => {
    try {
      const info = await getUserLimit(instance, accounts?.[0]);
      setUserLimitInfo(info);
      return info;
    } catch (err) {
      console.error("Failed to fetch user limit:", err);
      return null;
    }
  }, [instance, accounts]);

  const updateWarningMessage = useCallback((currentMembers: MemberDto[], limitInfo: typeof userLimitInfo) => {
    if (!limitInfo || limitInfo.maxUsers === 0) {
      setWarningMessage(null);
      return;
    }

    const remaining = limitInfo.maxUsers - currentMembers.length;

    if (remaining === 1) {
      setWarningMessage("Only 1 user slot left before reaching the maximum limit.");
    } else if (remaining === 0) {
      setWarningMessage("You have reached the maximum number of users. To add more users please contact your account manager");
    } else if (remaining < 0) {
      setWarningMessage(`You have exceeded the maximum number of users by ${Math.abs(remaining)}. Please contact your account manager.`);
    } else {
      setWarningMessage(null);
    }
    
    lastMembersCount.current = currentMembers.length;
  }, []);

  useEffect(() => {
    updateWarningMessage(members, userLimitInfo);
  }, [members, userLimitInfo, updateWarningMessage]);

  // OPTIMISTIC UPDATE: Remove user from local state immediately
  const removeMember = useCallback((userId: string) => {
    setMembers(prev => {
      const updated = prev.filter(m => m.id !== userId);
      // Update limit info to reflect new count
      setUserLimitInfo(info => ({
        ...info,
        currentUsers: updated.length
      }));
      return updated;
    });
  }, []);

  // OPTIMISTIC UPDATE: Add user to local state immediately
  const addMember = useCallback((newMember: MemberDto) => {
    setMembers(prev => {
      const updated = [...prev, newMember];
      setUserLimitInfo(info => ({
        ...info,
        currentUsers: updated.length
      }));
      return updated;
    });
  }, []);

  // Background sync: re-fetch to ensure consistency, but don't block UI
  const syncWithBackend = useCallback(async () => {
    try {
      const [fetchedMembers, limitInfo] = await Promise.all([
        fetchUsers(),
        fetchUserLimit()
      ]);
      if (limitInfo) {
        updateWarningMessage(fetchedMembers, limitInfo);
      }
    } catch (err) {
      console.error("Background sync failed:", err);
    }
  }, [fetchUsers, fetchUserLimit, updateWarningMessage]);

  useEffect(() => {
    const loadInitialData = async () => {
      setProjectsLoading(true);
      setProjectsError(null);
      try {
        await new Promise(resolve => setTimeout(resolve, 1000));
        const [fetchedMembers, limitInfo, fetchedProjects] = await Promise.all([
          fetchUsers(),
          fetchUserLimit(),
          getprojectforuser(instance, accounts?.[0])
        ]);
        setAllProjects(Array.isArray(fetchedProjects) ? fetchedProjects : []);
        if (limitInfo) {
          updateWarningMessage(fetchedMembers, limitInfo);
        }
      } catch (err) {
        setProjectsError('Failed to fetch projects.');
        setAllProjects([]);
      } finally {
        setProjectsLoading(false);
      }
    };
    loadInitialData();
  }, [fetchUsers, fetchUserLimit, updateWarningMessage, instance, accounts]);

  const searchParams = new URLSearchParams(location.search);
  const initialShowModal = searchParams.get('newUser') === '1';

  if ((loading && members.length === 0) || projectsLoading) {
    return (
      <div className="d-flex justify-content-center align-items-center py-5">
        <LoaderIcon />
      </div>
    );
  }

  if (!canManageUsers) {
    return (
      <AccessRestricted title='User Management' />
    );
  }

  return (
    <Row>
      <Col sm={12} as="h1" className="mb-3">
        User Management
      </Col>

      <Col xl={9}>
        {warningMessage && (
          <Alert variant='warning' className='shadow mb-5'>{warningMessage}</Alert>
        )}
        {projectsError && <div className="text-danger mb-3">{projectsError}</div>}
        <UsersTable 
          members={members} 
          userlimitinfo={userLimitInfo} 
          initialShowModal={initialShowModal} 
          highestPrivilegeRole={highestPrivilegeRole} 
          allProjects={allProjects}
          projectsLoading={projectsLoading}
          onRemoveMember={removeMember}
          onAddMember={addMember}
          onSync={syncWithBackend}
        />
        {fetchError && <div className="text-danger mt-3">Failed to load users.</div>}
      </Col>

      <Col xl={3}>
        <RecentActivity />
      </Col>
    </Row>
  );
};

export default UsersManagementPage;

